import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AccountSettings from "../components/profile/AccountSettings";
import ProfileDetails from "../components/profile/ProfileDetails";
import UserInformation from "../components/profile/UserInformation";
import Layout from "../components/Layout";

const Profile = () => {
  const [user, setUser] = useState({});
  const [username, setUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [editingUsername, setEditingUsername] = useState(false);  
  const [email, setEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({});
  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState("account");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

	// 加入 checkUsernameExists 函式
	const checkUsernameExists = async (username) => {
	  const res = await fetch(`${process.env.REACT_APP_API_URL}/users/check_username?username=${encodeURIComponent(username)}`, {
		headers: { Authorization: `Bearer ${token}` },
	  });
	  const data = await res.json();
	  return data.exists;
	};

  const fetchProfile = async () => {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setUser(data);
    setEmail(data.email || "");
    setNewEmail(data.email || "");
	setUsername(data.username || "");
	setNewUsername(data.username || "");
    setLoading(false);
  };

	// fetch profile 資料
	const fetchProfileDetails = async () => {
	  const res = await fetch(`${process.env.REACT_APP_API_URL}/users/me/profile`, {
		headers: { Authorization: `Bearer ${token}` },
	  });
	  const data = await res.json();
	  setProfile(data);
	};

	useEffect(() => {
	  fetchProfile();
	  fetchProfileDetails(); // 加載 profile 資訊
	}, []);

	const updateProfileDetails = async () => {
	  await fetch(`${process.env.REACT_APP_API_URL}/users/me/profile`, {
		method: "PUT",
		headers: {
		  "Content-Type": "application/json",
		  Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(profile),
	  });
	};

  const checkEmailExists = async (email) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/users/check_email?email=${encodeURIComponent(email)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    return data.exists;
  };

	useEffect(() => {
	  const fetchCountries = async () => {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/countries/countrieslist`);
		const data = await res.json();
		setCountries(data);
	  };
	  fetchCountries();
	}, []);

	const updateProfile = async () => {
	  if (editingUsername) {
		const usernameExists = await checkUsernameExists(newUsername);
		if (usernameExists) {
		  alert("This username is already taken");
		  return;
		}
	  }
	  
	  if (editingEmail) {
		const emailExists = await checkEmailExists(newEmail);
		if (emailExists) {
		  alert("This email has been used by another member");
		  return;
		}
	  }
	  if (editingPassword) {
		if (!oldPassword) {
		  alert("Please enter old password");
		  return;
		}
		if (newPassword !== confirmNewPassword) {
		  alert("The new password and the confirm new password do not match");
		  return;
		}

		const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
		if (!strongPasswordRegex.test(newPassword)) {
		  alert("Password must be at least 8 characters long and contain uppercase, lowercase, number and special character");
		  return;
		}		
	  }
 
	  const requestBody = {
		user_update: {
		  username: editingUsername ? newUsername : undefined,
		  email: editingEmail ? newEmail : undefined,
		  password: editingPassword ? newPassword : undefined,
		},
		old_password: oldPassword
	  };

	  //console.log("requestBody = ", requestBody);

	  const res = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
		method: "PUT",
		headers: {
		  "Content-Type": "application/json",
		  Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(requestBody),
	  });

	  if (res.ok) {
		await updateProfileDetails();
		alert("Update successful");
		navigate("/dashboard");
	  } else {
		const errorText = await res.text();
		console.log("Update failed，status:", res.status);
		console.log("Return content:", errorText);
		alert("Update failed: " + errorText);
	  }
	};

  const deleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete your account? Cannot be restored after deletion")) return;
    await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    alert("Account deleted");
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const cancelChanges = () => {
    if (window.confirm("Are you sure you want to cancel? The changed data will not be stored")) {
      navigate("/dashboard");
    }
  };

	useEffect(() => {
	  fetchProfile();
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-24 w-24"></div>
      </div>
    );
  }

  return (
  <Layout> 
    <div className="responsive-container max-w-2xl mx-auto py-6 space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-center text-primary mb-6 sm:mb-8">Member Profile Settings</h1>
		<div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg">
		  <div className="flex flex-col sm:grid sm:grid-cols-4 gap-6">
			{/* Left Side Tabs */}
			<div className="flex sm:flex-col gap-4 border-b sm:border-b-0 sm:border-r sm:pr-4 pb-4 sm:pb-0">
				<button
				  onClick={() => navigate("/dashboard")}
				  className="text-gray-600 hover:text-primary flex items-center gap-1"
				>
				  <ArrowLeft size={20} />
				  <span className="hidden sm:inline">Back</span>
				</button> 	
			  <button
				className={`w-full text-left px-3 py-2 rounded-lg font-semibold transition ${
				  activeTab === "account"
					? "bg-primary text-white"
					: "text-gray-700 hover:bg-gray-100"
				}`}
				onClick={() => setActiveTab("account")}
			  >
				Account
			  </button>
			  <button
				className={`w-full text-left px-3 py-2 rounded-lg font-semibold transition ${
				  activeTab === "profile"
					? "bg-primary text-white"
					: "text-gray-700 hover:bg-gray-100"
				}`}
				onClick={() => setActiveTab("profile")}
			  >
				Profile
			  </button>
			  <button
				className={`w-full text-left px-3 py-2 rounded-lg font-semibold transition ${
				  activeTab === "info"
					? "bg-primary text-white"
					: "text-gray-700 hover:bg-gray-100"
				}`}
				onClick={() => setActiveTab("info")}
			  >
				Information
			  </button>
			</div>

			{/* Right Side Content */}
			<div className="sm:col-span-3 space-y-6">
			  {activeTab === "account" && (
				<AccountSettings
				  profile={profile}
				  setProfile={setProfile}
				  username={username}
				  token={token}
				  editingUsername={editingUsername}
				  setEditingUsername={setEditingUsername}
				  newUsername={newUsername}
				  setNewUsername={setNewUsername}
				  editingEmail={editingEmail}
				  setEditingEmail={setEditingEmail}
				  email={email}
				  newEmail={newEmail}
				  setNewEmail={setNewEmail}
				  editingPassword={editingPassword}
				  setEditingPassword={setEditingPassword}
				  oldPassword={oldPassword}
				  setOldPassword={setOldPassword}
				  newPassword={newPassword}
				  setNewPassword={setNewPassword}
				  confirmNewPassword={confirmNewPassword}
				  setConfirmNewPassword={setConfirmNewPassword}
				/>
			  )}

			  {activeTab === "profile" && (
				<ProfileDetails
				  profile={profile}
				  setProfile={setProfile}
				  username={username}
				  countries={countries}
				  token={token}
				/>
			  )}

			  {activeTab === "info" && (
				<UserInformation
				  profile={profile}
				  setProfile={setProfile}
				  countries={countries}
				/>
			  )}

			  {/* Action Buttons */}
			  <div className="flex flex-col gap-3">
				<button
				  onClick={updateProfile}
				  className="btn-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-md"
				>
				  Save changes
				</button>

				<button
				  onClick={cancelChanges}
				  className="btn-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 rounded-md"
				>
				  Cancel changes
				</button>

				<button
				  onClick={deleteAccount}
				  className="btn-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2 rounded-md"
				>
				  Delete account
				</button>
			  </div>
			</div>
		  </div>
		</div>
	</div>
  </Layout>
  );
};

export default Profile;
